// useStaleRooms.ts — Live canister-backed stale-rooms hook.
//
// WHY this is now wired to the canister (was MOCK_ROOMS before 2026-05-22):
// D2/D3 shipped on dfx 0.25 in commits `7364987` (lobby) and `fc6d8b1`
// (admin) on 2026-05-11, removing the original "wait for dfx 0.30+" gate.
// The dominus_admin canister has been live on mainnet since 2026-05-19, so
// the launch-eve admin console can read real stale-room state and route
// destructive cleanup through the existing admin_force_finish_room method
// on lobby_canister (caller-must-be-admin per lobby/main.mo:2056).
//
// Threshold: 30 minutes (matches lobby_canister.STALE_WAITING_TTL_NS). The
// lobby auto-prunes #waiting rooms at the same threshold via the hourly
// timer registered at lobby/main.mo:2071. This dashboard surfaces what the
// lobby has not yet pruned (timer cadence + #playing / #finished rooms
// that the auto-prune does not touch) so the operator can intervene.
// Optional UI control for tighter/looser thresholds deferred to v1.1.
//
// Refresh: every 60s on a setInterval timer, matching the cadence used by
// useCanisterHealth and useAdminMetrics (the other admin polling hooks).

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Identity } from '@dfinity/agent';
import {
  createAdminAgent,
  createAdminActor,
  createLobbyAdminActor,
  type LobbyRoomInfo,
} from '../lib/adminAgent';

// 30 minutes in nanoseconds. Matches lobby_canister.STALE_WAITING_TTL_NS.
const STALE_THRESHOLD_NS = 30n * 60n * 1_000_000_000n;
const POLL_INTERVAL_MS = 60_000;

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type RoomStatus = 'waiting' | 'playing' | 'finished';
export type RoomVisibility = 'private' | 'public';

export interface StaleRoomInfo {
  roomId: string;
  hostName: string;
  hostPrincipal: string;
  status: RoomStatus;
  ageMinutes: number;  // minutes since room was created
  playerCount: number; // current occupancy (1–4)
  visibility: RoomVisibility;
}

export interface UseStaleRoomsReturn {
  rooms: StaleRoomInfo[];
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: Date | null;
  refresh: () => void;
  forceClose: (roomId: string) => Promise<void>;
  bulkCleanup: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Mappers — canister tuple -> StaleRoomInfo
// ---------------------------------------------------------------------------

function mapStatus(s: LobbyRoomInfo['status']): RoomStatus {
  if ('waiting' in s || 'full' in s) return 'waiting';
  if ('playing' in s || 'playing_open' in s) return 'playing';
  return 'finished';
}

function mapVisibility(v: LobbyRoomInfo['visibility']): RoomVisibility {
  return 'public_room' in v ? 'public' : 'private';
}

/// Convert ns-since-epoch (canister `Int`) to whole minutes since the
/// reference instant. The canister uses System.Time.now() which is also
/// ns-since-epoch, so JS Date.now()*1_000_000 is the same epoch.
function ageInMinutes(createdAtNs: bigint, gameStartedAtOpt: [] | [bigint], status: RoomStatus): number {
  // For #playing rooms, use gameStartedAt if present (matches the canister's
  // own freshness anchor at list_stale_rooms main.mo:1685-1689). For #waiting
  // rooms the canister falls back to createdAt because lastHostHeartbeat is
  // not exposed on the public RoomInfo (documented at main.mo:1639-1647).
  const anchorNs = (status === 'playing' && gameStartedAtOpt.length === 1)
    ? gameStartedAtOpt[0]
    : createdAtNs;
  const nowMs = Date.now();
  // bigint -> Number conversion: ns / 1_000_000 -> ms. Safe for any timestamp
  // within the next ~285k years (Number.MAX_SAFE_INTEGER / (365*24*60*60*1000)).
  const anchorMs = Number(anchorNs / 1_000_000n);
  const ageMinutes = Math.max(0, Math.floor((nowMs - anchorMs) / 60_000));
  return ageMinutes;
}

function mapToStaleRoomInfo(
  roomId: string,
  info: LobbyRoomInfo,
): StaleRoomInfo {
  const status = mapStatus(info.status);
  return {
    roomId,
    hostName: info.hostName,
    hostPrincipal: info.hostPrincipal.toText(),
    status,
    ageMinutes: ageInMinutes(info.createdAt, info.gameStartedAt, status),
    playerCount: Number(info.playerCount),
    visibility: mapVisibility(info.visibility),
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useStaleRooms(identity: Identity | null): UseStaleRoomsReturn {
  const [rooms, setRooms] = useState<StaleRoomInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  const fetchRooms = useCallback(async () => {
    if (!identity) return;
    setIsLoading(true);
    setError(null);

    try {
      const agent = await createAdminAgent(identity);
      const adminActor = createAdminActor(agent);
      const result = await adminActor.list_stale_rooms(STALE_THRESHOLD_NS);

      if (!isMountedRef.current) return;

      if ('ok' in result) {
        setRooms(result.ok.map(([id, info]) => mapToStaleRoomInfo(id, info)));
        setLastFetchedAt(new Date());
      } else {
        setError(result.err);
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to fetch stale rooms.';
      setError(message);
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [identity]);

  const refresh = useCallback(() => {
    fetchRooms();
  }, [fetchRooms]);

  const forceClose = useCallback(async (roomId: string) => {
    if (!identity) return;
    try {
      const agent = await createAdminAgent(identity);
      const lobby = createLobbyAdminActor(agent);
      const result = await lobby.admin_force_finish_room(roomId);
      if (!isMountedRef.current) return;
      if ('err' in result) {
        setError(result.err);
        return;
      }
      // Optimistic remove from local state; the next 60s poll will
      // re-confirm against the canister.
      setRooms((prev) => prev.filter((r) => r.roomId !== roomId));
    } catch (err) {
      if (!isMountedRef.current) return;
      const message = err instanceof Error ? err.message : 'Force-close failed.';
      setError(message);
    }
  }, [identity]);

  const bulkCleanup = useCallback(async () => {
    if (!identity) return;
    const snapshot = rooms; // snapshot before iteration to avoid state mutation during loop
    try {
      const agent = await createAdminAgent(identity);
      const lobby = createLobbyAdminActor(agent);
      // Sequential rather than Promise.all -- a single agent serialises calls
      // anyway, and sequential surfaces partial-failure cleanly (the first
      // error sets `error` and stops; the dashboard re-polls in 60s).
      const succeeded: string[] = [];
      for (const room of snapshot) {
        const result = await lobby.admin_force_finish_room(room.roomId);
        if ('err' in result) {
          if (isMountedRef.current) setError(result.err);
          break;
        }
        succeeded.push(room.roomId);
      }
      if (!isMountedRef.current) return;
      // Remove all rooms that succeeded from local state.
      const succeededSet = new Set(succeeded);
      setRooms((prev) => prev.filter((r) => !succeededSet.has(r.roomId)));
    } catch (err) {
      if (!isMountedRef.current) return;
      const message = err instanceof Error ? err.message : 'Bulk cleanup failed.';
      setError(message);
    }
  }, [identity, rooms]);

  useEffect(() => {
    if (!identity) return;
    isMountedRef.current = true;
    fetchRooms();
    timerRef.current = setInterval(fetchRooms, POLL_INTERVAL_MS);
    return () => {
      isMountedRef.current = false;
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [identity, fetchRooms]);

  return { rooms, isLoading, error, lastFetchedAt, refresh, forceClose, bulkCleanup };
}
